import { useEffect, useState } from "react";
import { generateClient } from "aws-amplify/api";
import { MapView } from "@aws-amplify/ui-react-geo";
import { Marker } from "react-map-gl";

import "@aws-amplify/ui-react-geo/styles.css";

import { gqlGetPartnerCurrentLocation } from "../../graphql/query/getPartnerCurrentLocation";
import { gqlSubscribeFilteredPartnerLocation } from "../../graphql/subscription/subscribeFilteredPartnerLocation";

const graphQLClient = generateClient();

export const JobLocationMap = ({ job }) => {
  const [partnerLocation, setPartnerLocation] = useState(null);

  const premiseLatitude = Number(
    job?.premise?.location?.latitude,
  );

  const premiseLongitude = Number(
    job?.premise?.location?.longitude,
  );

  const partnerIsTravelling =
    job?.status === "JOB:ROUTE:INPROGRESS";

  useEffect(() => {
    // Do not track the partner after arrival
    if (!partnerIsTravelling) {
      setPartnerLocation(null);
      return;
    }

    // Get the partner's latest saved location
    const loadPartnerLocation = async () => {
      try {
        const response = await graphQLClient.graphql({
          query: gqlGetPartnerCurrentLocation,
          variables: {
            user_id: job.user_id,
            job_id: job.job_id,
          },
        });

        const location =
          response.data.getPartnerCurrentLocation;

        if (location?.position) {
          setPartnerLocation(location.position);
        }
      } catch (error) {
        console.error(
          "Unable to get partner location",
          error,
        );
      }
    };

    loadPartnerLocation();

    // Listen for new partner locations
    const subscription = graphQLClient
      .graphql({
        query: gqlSubscribeFilteredPartnerLocation,
        variables: {
          filter: JSON.stringify({
            resource_name: {
              eq: `user:${job.user_id}:job:${job.job_id}:current_location`,
            },
          }),
        },
      })
      .subscribe({
        next: ({ data }) => {
          const location =
            data.subscribeFilteredPartnerLocation;

          if (location?.position) {
            setPartnerLocation(location.position);
          }
        },

        error: (error) => {
          console.error(
            "Partner location subscription error",
            error,
          );
        },
      });

    // Stop listening when leaving the page
    return () => {
      subscription.unsubscribe();
    };
  }, [
    job.user_id,
    job.job_id,
    partnerIsTravelling,
  ]);

  const locationIsValid =
    Number.isFinite(premiseLatitude) &&
    Number.isFinite(premiseLongitude);

  if (!locationIsValid) {
    return (
      <div className="flex h-64 items-center justify-center bg-slate-100 text-sm text-slate-500">
        Premise location is unavailable
      </div>
    );
  }

  return (
    <div className="h-64 w-full overflow-hidden rounded-lg">
      <MapView
        initialViewState={{
          latitude: premiseLatitude,
          longitude: premiseLongitude,
          zoom: 14,
        }}
        dragPan={false}
        dragRotate={false}
        scrollZoom={false}
        doubleClickZoom={false}
        touchZoomRotate={false}
        touchPitch={false}
        keyboard={false}
        boxZoom={false}
        style={{
          width: "100%",
          height: "100%",
        }}
      >
        {/* Premise marker */}
        <Marker
          latitude={premiseLatitude}
          longitude={premiseLongitude}
          color="#dc2626"
        />

        {/* Show partner marker only while travelling */}
        {partnerIsTravelling &&
          partnerLocation && (
            <Marker
              latitude={Number(
                partnerLocation.latitude,
              )}
              longitude={Number(
                partnerLocation.longitude,
              )}
              color="#2563eb"
            />
          )}
      </MapView>
    </div>
  );
};
